import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckSquare, Lock, LockOpen, Plus, Search, X } from "lucide-react";
import QuickCalculator from "@/components/QuickCalculator";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BATCH_STATUS_META, LOCATIONS, REGIONS, fmtRate } from "@/lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import ProductRow from "@/components/ProductRow";
import ProductForm from "@/components/ProductForm";
import ProductSheet from "@/components/ProductSheet";
import RecordPurchase from "@/components/RecordPurchase";
import { NumInput } from "@/components/NumInput";
import ShoppingList from "@/components/ShoppingList";
import BatchSummaryView from "@/components/BatchSummaryView";

const STATUS_FILTERS = [
  { v: "all", l: "All" },
  { v: "unassigned", l: "Unassigned" },
  { v: "not_purchased", l: "Not Purchased" },
  { v: "partial", l: "Partial" },
  { v: "purchased", l: "Purchased" },
];

export default function BatchDetail() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user.role === "owner";

  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLocation, setBulkLocation] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sheetProduct, setSheetProduct] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [purchaseProduct, setPurchaseProduct] = useState(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [shopLocation, setShopLocationState] = useState(() => localStorage.getItem(`ht_shop_loc_${batchId}`) || "all");
  const [shopStatus, setShopStatus] = useState("all");
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupRegion, setSetupRegion] = useState("");
  const [setupRate, setSetupRate] = useState("");
  const [setupSaving, setSetupSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const [purchaseInitialQty, setPurchaseInitialQty] = useState(0);

  const closed = data?.batch?.status === "closed";
  const canEdit = isOwner || user.role === "purchaser";
  const canPurchase = canEdit && !closed;
  const summaryRefreshKey = data ? `${data.batch.status}:${data.batch.closed_at || ""}:${data.batch.reopened_at || ""}` : "";

  const saveCurrencySetup = async () => {
    if (!setupRegion || !(parseFloat(setupRate) > 0)) return;
    setSetupSaving(true);
    try {
      await api.patch(`/batches/${batchId}`, { region: setupRegion, exchange_rate_to_idr: parseFloat(setupRate) });
      toast.success("Batch currency configured");
      setSetupOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSetupSaving(false);
    }
  };

  const closeBatch = async () => {
    if (!confirmClose) {
      setConfirmClose(true);
      return;
    }
    setClosing(true);
    try {
      await api.post(`/batches/${batchId}/close`);
      toast.success("Batch closed — summary is now finalized");
      setConfirmClose(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setConfirmClose(false);
    } finally {
      setClosing(false);
    }
  };

  const reopenBatch = async () => {
    if (!reopenReason.trim()) return;
    setReopening(true);
    try {
      await api.post(`/batches/${batchId}/reopen`, { reason: reopenReason.trim() });
      toast.success("Batch reopened — operations unlocked");
      setReopenOpen(false);
      setReopenReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReopening(false);
    }
  };

  const setShopLocation = (v) => {
    setShopLocationState(v);
    localStorage.setItem(`ht_shop_loc_${batchId}`, v);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/batches/${batchId}/products`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  const products = data?.products || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (statusFilter === "unassigned" && p.buying_location) return false;
      if (["not_purchased", "partial", "purchased"].includes(statusFilter) && p.status !== statusFilter) return false;
      if (locationFilter === "unassigned" && p.buying_location) return false;
      if (locationFilter !== "all" && locationFilter !== "unassigned" && p.buying_location !== locationFilter) return false;
      return true;
    });
  }, [products, search, statusFilter, locationFilter]);

  const locationOptions = useMemo(
    () => [...new Set([...LOCATIONS, ...products.map((p) => p.buying_location).filter(Boolean)])],
    [products]
  );

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openSheet = (p) => {
    setSheetProduct(p);
    setSheetOpen(true);
  };

  const openPurchase = (p, qty) => {
    setPurchaseInitialQty(qty > 0 ? qty : 0);
    setPurchaseProduct(p);
    setPurchaseOpen(true);
  };

  const applyBulk = async (loc) => {
    try {
      await api.post("/products/bulk-location", { product_ids: [...selected], buying_location: loc || null });
      toast.success(`Location updated for ${selected.size} product${selected.size > 1 ? "s" : ""}`);
      setBulkOpen(false);
      setBulkLocation("");
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) {
    return (
      <div className="grid place-items-center py-24" data-testid="batch-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const { batch, summary } = data;

  return (
    <div data-testid="batch-detail-page">
      <button
        data-testid="back-to-batches"
        onClick={() => navigate("/")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All batches
      </button>

      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight" data-testid="batch-name">{batch.name}</h1>
          <span
            data-testid="batch-op-status"
            className={`rounded-md border px-2 py-0.5 text-xs font-bold ${(BATCH_STATUS_META[batch.operational_status] || BATCH_STATUS_META.open).badge}`}
          >
            {(BATCH_STATUS_META[batch.operational_status] || BATCH_STATUS_META.open).label}
          </span>
          <span className="ml-auto"><QuickCalculator batch={batch} /></span>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="batch-currency-info">
          {batch.region || batch.destination || "—"}
          {batch.currency_code && ` · ${batch.currency_code} ${batch.currency_symbol}`}
          {user.role !== "viewer" && batch.exchange_rate_to_idr != null && ` · ${fmtRate(batch.currency_symbol, batch.exchange_rate_to_idr)}`}
        </p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="batch-actions">
          {isOwner && !closed && (
            <Button
              variant={confirmClose ? "destructive" : "outline"}
              data-testid="close-batch-button"
              className="h-10"
              onClick={closeBatch}
              disabled={closing}
            >
              <Lock className="h-4 w-4" /> {closing ? "Closing…" : confirmClose ? "Confirm Close Batch" : "Close Batch"}
            </Button>
          )}
          {isOwner && closed && (
            <Button variant="outline" data-testid="reopen-batch-button" className="h-10" onClick={() => setReopenOpen(true)}>
              <LockOpen className="h-4 w-4" /> Reopen Batch
            </Button>
          )}
        </div>
        {closed && (
          <div data-testid="batch-closed-banner" className="mt-3 rounded-md border border-stone-300 bg-stone-100 px-3 py-2 text-xs font-semibold text-stone-700">
            BATCH CLOSED{batch.closed_by && ` by ${batch.closed_by}`}
            {batch.closed_at && ` · ${format(new Date(batch.closed_at), "d MMM yyyy, HH:mm")}`} — purchasing, editing and receiving are locked. Historical records are preserved.
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="batch-summary">
          <span className="rounded-md border bg-card px-2.5 py-1 text-xs font-semibold">{summary.product_count} products</span>
          <span data-testid="summary-ordered" className="rounded-md border bg-card px-2.5 py-1 text-xs font-semibold">
            Ordered {summary.ordered_units}
          </span>
          <span data-testid="summary-purchased" className="rounded-md border bg-card px-2.5 py-1 text-xs font-semibold">
            Purchased {summary.purchased_units}
          </span>
          {summary.remaining_units > 0 ? (
            <span data-testid="summary-still-to-buy" className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
              Still to buy {summary.remaining_units}
            </span>
          ) : summary.product_count > 0 ? (
            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              All purchased
            </span>
          ) : null}
          {summary.buffer_units > 0 && (
            <span data-testid="summary-buffer" className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              Buffer {summary.buffer_units}
            </span>
          )}
          {summary.unassigned_count > 0 && (
            <span data-testid="summary-unassigned" className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600">
              {summary.unassigned_count} UNASSIGNED
            </span>
          )}
        </div>
      </div>

      {summary.unassigned_count > 0 && !closed && (
        <div data-testid="unassigned-warning" className="mb-4 rounded-md border border-red-300 bg-red-50 p-3.5">
          <p className="text-sm font-extrabold text-red-700">
            {summary.unassigned_count} product{summary.unassigned_count === 1 ? "" : "s"} still need a Buying Location.
          </p>
          <p className="mt-0.5 text-xs font-semibold text-red-600">Assign locations before shopping.</p>
        </div>
      )}

      {!batch.region && (
        <div data-testid="currency-setup-banner" className="mb-5 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-800">Batch currency setup required</p>
          {isOwner && !closed && (
            <Button data-testid="currency-setup-button" size="sm" className="h-10 px-4" onClick={() => setSetupOpen(true)}>
              Configure
            </Button>
          )}
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="grid h-12 w-full grid-cols-3">
          <TabsTrigger data-testid="tab-products" value="products" className="h-10 text-sm font-semibold">
            Products
          </TabsTrigger>
          <TabsTrigger data-testid="tab-shopping-list" value="shopping" className="h-10 text-sm font-semibold">
            Shopping List
          </TabsTrigger>
          <TabsTrigger data-testid="tab-summary" value="summary" className="h-10 text-sm font-semibold">
            Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-3 pt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="product-search-input"
              className="h-12 pl-9"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.v}
                data-testid={`filter-${f.v}`}
                onClick={() => setStatusFilter(f.v)}
                className={`h-9 shrink-0 rounded-full border px-3.5 text-xs font-semibold transition-colors active:scale-[0.98] ${
                  statusFilter === f.v
                    ? f.v === "unassigned"
                      ? "border-red-500 bg-red-500 text-white"
                      : "border-primary bg-primary text-white"
                    : "bg-card text-stone-600 hover:bg-accent"
                }`}
              >
                {f.l}
              </button>
            ))}
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger data-testid="location-filter-select" className="h-9 w-[150px] shrink-0 rounded-full text-xs font-semibold">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="location-filter-all">All locations</SelectItem>
                <SelectItem value="unassigned" data-testid="location-filter-unassigned">UNASSIGNED</SelectItem>
                {locationOptions.map((l) => (
                  <SelectItem key={l} value={l} data-testid={`location-filter-${l.toLowerCase().replace(/\s/g, "-")}`}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canEdit && !closed && (
            <div className="flex gap-2">
              <Button data-testid="add-product-button" className="h-11 flex-1" onClick={() => { setEditing(null); setFormOpen(true); }}>
                <Plus className="h-4 w-4" /> Add Product
              </Button>
              {isOwner && (
                <Button
                  variant={selectMode ? "secondary" : "outline"}
                  data-testid="select-mode-toggle"
                  className="h-11"
                  onClick={() => {
                    setSelectMode((s) => !s);
                    setSelected(new Set());
                  }}
                >
                  {selectMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
                  {selectMode ? "Cancel" : "Select"}
                </Button>
              )}
            </div>
          )}

          {filtered.length === 0 ? (
            <p data-testid="products-empty" className="rounded-md border border-dashed py-16 text-center text-sm text-muted-foreground">
              {products.length === 0 ? "No products in this batch yet" : "No products match your filters"}
            </p>
          ) : (
            <div data-testid="product-list" className="space-y-2.5">
              {filtered.map((p) => (
                <ProductRow
                  key={p.id}
                  product={p}
                  selectable={selectMode}
                  selected={selected.has(p.id)}
                  onToggleSelect={toggleSelect}
                  onOpen={openSheet}
                  onPurchase={openPurchase}
                  canPurchase={canPurchase}
                  canChangeLocation={isOwner && !closed}
                  region={data.batch.region}
                  onLocationSaved={load}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="shopping" className="pt-3">
          <ShoppingList
            products={products}
            canPurchase={canPurchase}
            onPurchase={openPurchase}
            location={shopLocation}
            onLocationChange={setShopLocation}
            status={shopStatus}
            onStatusChange={setShopStatus}
          />
        </TabsContent>

        <TabsContent value="summary" className="pt-3">
          <BatchSummaryView batchId={batchId} refreshKey={summaryRefreshKey} />
        </TabsContent>
      </Tabs>

      {selectMode && selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-20 z-40 px-4 md:bottom-6">
          <div className="mx-auto flex max-w-5xl items-center gap-3 rounded-md border bg-white/90 p-3 shadow-lg backdrop-blur-xl">
            <span data-testid="bulk-selected-count" className="text-sm font-semibold">
              {selected.size} selected
            </span>
            <Button data-testid="bulk-assign-button" className="ml-auto h-11" onClick={() => setBulkOpen(true)}>
              Assign Location
            </Button>
          </div>
        </div>
      )}

      <Drawer open={bulkOpen} onOpenChange={setBulkOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Assign buying location</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8">
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                data-testid="bulk-location-input"
                className="h-12"
                placeholder="e.g. Costco"
                value={bulkLocation}
                onChange={(e) => setBulkLocation(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                {locationOptions.map((l) => (
                  <button
                    key={l}
                    type="button"
                    data-testid={`bulk-location-chip-${l.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setBulkLocation(l)}
                    className={`h-9 rounded-full border px-3 text-xs font-semibold transition-colors active:scale-[0.98] ${
                      bulkLocation === l ? "border-primary bg-primary text-white" : "text-stone-600 hover:bg-accent"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="bulk-apply-button" className="h-12 flex-1" onClick={() => applyBulk(bulkLocation)} disabled={!bulkLocation.trim()}>
                Apply to {selected.size} product{selected.size > 1 ? "s" : ""}
              </Button>
              <Button variant="outline" data-testid="bulk-unassign-button" className="h-12" onClick={() => applyBulk(null)}>
                Unassign
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={setupOpen} onOpenChange={setSetupOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Batch currency setup</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8">
            <div className="space-y-2">
              <Label>Region</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(REGIONS).map((r) => (
                  <button
                    key={r}
                    type="button"
                    data-testid={`setup-region-${r.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setSetupRegion(r)}
                    className={`h-12 rounded-md border text-sm font-semibold transition-colors active:scale-[0.98] ${
                      setupRegion === r ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Currency (auto)</Label>
              <div data-testid="setup-currency" className="flex h-12 items-center rounded-md border bg-muted px-3 text-sm font-semibold text-stone-700">
                {setupRegion ? `${REGIONS[setupRegion].code} ${REGIONS[setupRegion].symbol}` : "Select a region first"}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Operational Exchange Rate to IDR (required)</Label>
              <NumInput
                data-testid="setup-rate-input"
                mode="decimal"
                className="h-12"
                placeholder="e.g. 110"
                value={setupRate}
                onValue={setSetupRate}
              />
            </div>
            <Button
              data-testid="setup-save-button"
              className="h-12 w-full text-base"
              onClick={saveCurrencySetup}
              disabled={setupSaving || !setupRegion || !(parseFloat(setupRate) > 0)}
            >
              {setupSaving ? "Saving…" : "Save Currency Setup"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <ProductForm
        batchId={batchId}
        product={editing}
        open={formOpen}
        batchRegion={batch.region}
        onOpenChange={setFormOpen}
        onSaved={() => {
          load();
          setSheetOpen(false);
        }}
      />
      <ProductSheet
        product={products.find((p) => p.id === sheetProduct?.id) || sheetProduct}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onChanged={load}
        onEdit={(p) => {
          setEditing(p);
          setFormOpen(true);
        }}
        onPurchase={openPurchase}
        canPurchase={canPurchase}
        canEdit={isOwner && !closed}
        isOwner={isOwner}
        locked={closed}
      />
      <RecordPurchase
        product={products.find((p) => p.id === purchaseProduct?.id) || purchaseProduct}
        open={purchaseOpen}
        onOpenChange={setPurchaseOpen}
        onSaved={load}
        batch={batch}
        isOwner={isOwner}
        initialQty={purchaseInitialQty}
      />

      <Drawer open={reopenOpen} onOpenChange={setReopenOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Reopen Batch</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8">
            <p className="text-sm text-muted-foreground">
              Reopening unlocks purchasing, editing and receiving again. Historical records stay untouched. Your reason is recorded in the batch lifecycle history.
            </p>
            <div className="space-y-2">
              <Label>Reason (required)</Label>
              <Input
                data-testid="reopen-reason-input"
                className="h-12"
                placeholder="e.g. Late shipment arrived"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
            </div>
            <Button
              data-testid="reopen-confirm-button"
              className="h-12 w-full text-base"
              onClick={reopenBatch}
              disabled={reopening || !reopenReason.trim()}
            >
              {reopening ? "Reopening…" : "Confirm Reopen"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
